import React, { useEffect } from "react";
import { useLocation, Link, useHistory } from "react-router-dom";
import {
  useSuperAppConfig,
  getSuperAppConfigSync,
} from "../lib/superappConfig";
import LayoutRenderer from "./LayoutRenderer";
import { useMyContext } from "../MyContext";
import { FaEdit, FaMagic, FaPlus, FaCode, FaLayerGroup } from "react-icons/fa";

function resolveRouteKey(pathname) {
  const p = String(pathname || "/").replace(/\/+$/, "");
  const key = p === "" || p === "/" ? "home" : p.replace(/^\//, "");
  return key.toLowerCase();
}

export default function LowCodePage({ appId: propAppId }) {
  const { pathname } = useLocation();
  const history = useHistory();
  const routeKey = resolveRouteKey(pathname);

  // Vik (Jan 22, 2026): Prefer URL appId over prop to ensure we always use the correct app
  // when navigating between pages. This fixes blank screen issue when clicking newly created menu items.
  const urlAppId = new URLSearchParams(window.location.search).get("appId");
  const appId =
    urlAppId || propAppId || process.env.REACT_APP_SUPERAPP_ID || "Ben-test";

  /* Vik (Jan 21, 2026) : Correctly retrieve user data from context (userId -> user, userData -> userDeep) */
  const { userId, userData } = useMyContext();
  const user = userId;
  const userDeep = userData;

  const cfgRaw = useSuperAppConfig(appId) || getSuperAppConfigSync(appId);
  const cfg = cfgRaw && cfgRaw.config ? cfgRaw.config : cfgRaw;
  const screen = cfg?.screens?.[routeKey];

  // Check if screen doesn't exist OR exists but has no content/structure
  const hasNoContent =
    !screen ||
    (screen.type === "layout" &&
      (!screen.structure ||
        !screen.blocks ||
        Object.keys(screen.blocks).length === 0)) ||
    (screen.type === "html" && !screen.html) ||
    (screen.type === "text" && !screen.value);

  // Premium Empty State Component
  const renderEmptyState = () => {
    return (
      <div className="empty-page-state">
        <div className="empty-state-content">
          <div className="icon-circle">
            <FaLayerGroup />
          </div>
          {/* Vik (Jan 23, 2026) : Highlight the route/menu name with elegant pill style for better visibility */}
          <h1>
            Start Building{" "}
            <span className="route-name-highlight">{routeKey}</span>
          </h1>
          <p>This page is currently empty. Choose how you want to start.</p>

          <div className="action-grid">
            <div
              className="action-card"
              onClick={() =>
                (window.location.href = `/page-editor?appId=${appId}&page=${routeKey}`)
              }
            >
              <div className="card-icon">
                <FaMagic />
              </div>
              <h3>Visual Editor</h3>
              <p>Drag and drop blocks to build your page visually.</p>
            </div>

            <div
              className="action-card"
              onClick={() =>
                (window.location.href = `/page-editor?appId=${appId}&page=${routeKey}&mode=code`)
              }
            >
              <div className="card-icon">
                <FaCode />
              </div>
              <h3>Code Editor</h3>
              <p>Edit the raw JSON configuration for full control.</p>
            </div>

            <div
              className="action-card"
              onClick={() => alert("Templates coming soon!")}
            >
              <div className="card-icon">
                <FaPlus />
              </div>
              <h3>Use Template</h3>
              <p>
                Start with a pre-built layout for dashboards or landing pages.
              </p>
            </div>
          </div>
        </div>

        {/* Vik (Jan 22, 2026) : Dark theme CSS to match AppSelector and maintain UI consistency */}
        <style>{`
          .empty-page-state {
            min-height: 60vh;
            display: flex;
            align-items: center;
            justify-content: center;
            background: #0b1221;
            padding: 40px;
            font-family: 'Inter', sans-serif;
          }
          .empty-state-content {
            text-align: center;
            max-width: 800px;
          }
          .icon-circle {
            width: 80px;
            height: 80px;
            background: rgba(118, 75, 162, 0.2);
            color: #a78bfa;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 32px;
            margin: 0 auto 24px;
            border: 1px solid rgba(118, 75, 162, 0.3);
          }
          .empty-state-content h1 {
            font-weight: 800;
            color: #fff;
            margin-bottom: 12px;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
          }
          /* Vik (Jan 23, 2026) : Elegant pill-style highlight for the route/menu name */
          .route-name-highlight {
            display: inline-block;
            background: linear-gradient(135deg, rgba(118, 75, 162, 0.3) 0%, rgba(102, 126, 234, 0.3) 100%);
            color: #c4b5fd;
            padding: 4px 16px;
            margin-left: 8px;
            border-radius: 20px;
            border: 1px solid rgba(167, 139, 250, 0.4);
            font-weight: 600;
            letter-spacing: 0.02em;
            -webkit-text-fill-color: #c4b5fd;
            text-shadow: 0 0 20px rgba(167, 139, 250, 0.5);
          }
          .empty-state-content > p {
            color: rgba(255,255,255,0.7);
            font-size: 1.1rem;
            margin-bottom: 40px;
          }
          .action-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
            gap: 24px;
          }
          .action-card {
            background: #1a2035;
            padding: 32px 24px;
            border-radius: 16px;
            box-shadow: 0 4px 20px rgba(0,0,0,0.3);
            cursor: pointer;
            transition: all 0.3s ease;
            border: 1px solid rgba(255,255,255,0.05);
          }
          .action-card:hover {
            transform: translateY(-5px);
            box-shadow: 0 12px 30px rgba(0,0,0,0.4);
            border-color: #764ba2;
            background: #222845;
          }
          .card-icon {
            font-size: 24px;
            color: #a78bfa;
            margin-bottom: 16px;
          }
          .action-card h3 {
            font-size: 18px;
            font-weight: 700;
            margin-bottom: 8px;
            color: #fff;
          }
          .action-card p {
            font-size: 14px;
            color: rgba(255,255,255,0.5);
            margin: 0;
            line-height: 1.5;
          }
        `}</style>
      </div>
    );
  };

  // Helper function to render Edit Page button
  const renderEditButton = () => {
    const appIdParam =
      new URLSearchParams(window.location.search).get("appId") || appId;
    const pageEditorUrl = `/page-editor/${appIdParam}?pageKey=${routeKey}`;
    const isDev =
      window.location.href.includes("localhost") ||
      window.location.href.includes("local") ||
      window.location.href.includes("dev");
    const isSystemAdmin =
      userDeep?.roles?.toLowerCase().indexOf("system_admin") >= 0;

    // Show edit button in dev mode or for system admins
    if (!isDev && !isSystemAdmin) return null;

    return (
      <div
        style={{
          position: "fixed",
          top: "80px",
          right: "20px",
          zIndex: 1000,
        }}
      >
        <Link
          to={pageEditorUrl}
          style={{
            display: "inline-block",
            padding: "10px 16px",
            background: "#2196f3",
            color: "#fff",
            textDecoration: "none",
            borderRadius: "6px",
            fontSize: "14px",
            fontWeight: "bold",
            boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
            transition: "all 0.2s",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "#1976d2";
            e.currentTarget.style.transform = "scale(1.05)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "#2196f3";
            e.currentTarget.style.transform = "scale(1)";
          }}
        >
          ✏️ Edit Page
        </Link>
      </div>
    );
  };

  if (hasNoContent) {
    return (
      <>
        {renderEmptyState()}
        {/* Keep the old debug info hidden or minimal if needed */}
      </>
    );
  }

  // Minimal types: text, html, list
  if (screen.type === "text") {
    return (
      <>
        {renderEditButton()}
        <div
          className="custom-container"
          style={{ paddingTop: 24, color: "#fff" }}
        >
          <h2 style={{ color: "#fff" }}>{screen.title || routeKey}</h2>
          <p style={{ color: "#fff" }}>{screen.value || ""}</p>
        </div>
      </>
    );
  }

  if (screen.type === "html") {
    return (
      <>
        {renderEditButton()}
        <div
          className="custom-container"
          style={{ paddingTop: 24, color: "#fff" }}
        >
          <h2 style={{ color: "#fff" }}>{screen.title || routeKey}</h2>
          <div
            dangerouslySetInnerHTML={{ __html: screen.html || "" }}
            style={{ color: "#fff" }}
          />
        </div>
      </>
    );
  }

  if (screen.type === "markdown") {
    const toHtml = (md) => {
      if (!md) return "";
      // super-lightweight: convert line breaks and bare links
      const withBreaks = String(md).replace(/\n/g, "<br/>");
      const withLinks = withBreaks.replace(
        /(https?:\/\/[^\s<]+)/g,
        '<a href="$1" target="_blank" rel="noreferrer" style="color: #fff;">$1<\/a>',
      );
      return withLinks;
    };
    return (
      <>
        {renderEditButton()}
        <div
          className="custom-container"
          style={{ paddingTop: 24, color: "#fff" }}
        >
          <h2 style={{ color: "#fff" }}>{screen.title || routeKey}</h2>
          <div
            dangerouslySetInnerHTML={{
              __html: toHtml(screen.text || screen.value || ""),
            }}
            style={{ color: "#fff" }}
          />
        </div>
      </>
    );
  }

  if (screen.type === "list" && Array.isArray(screen.items)) {
    return (
      <>
        {renderEditButton()}
        <div
          className="custom-container"
          style={{ paddingTop: 24, color: "#fff" }}
        >
          <h2 style={{ color: "#fff" }}>{screen.title || routeKey}</h2>
          <ul style={{ color: "#fff" }}>
            {screen.items.map((it, i) => (
              <li key={i} style={{ color: "#fff" }}>
                {typeof it === "string" ? it : JSON.stringify(it)}
              </li>
            ))}
          </ul>
        </div>
      </>
    );
  }

  // If screen has layout structure, render it using LayoutRenderer
  if (screen.type === "layout" && screen.structure && screen.blocks) {
    return (
      <>
        {renderEditButton()}
        <LayoutRenderer layout={screen} user={user} userDeep={userDeep} />
      </>
    );
  }

  // New blocks
  if (screen.type === "hero") {
    return (
      <>
        {renderEditButton()}
        <div
          className="custom-container"
          style={{ padding: "48px 0", color: "#fff" }}
        >
          <div style={{ textAlign: "center" }}>
            <h1 style={{ marginBottom: 12, color: "#fff" }}>
              {screen.title || ""}
            </h1>
            <p style={{ opacity: 0.9, color: "#fff" }}>
              {screen.subtitle || screen.text || ""}
            </p>
            {screen.cta && screen.cta.href && (
              <a
                className="primary-gradient-btn"
                href={screen.cta.href}
                target={screen.cta.target || "_self"}
                style={{ display: "inline-block", marginTop: 16 }}
                rel="noreferrer"
              >
                {screen.cta.label || "Learn more"}
              </a>
            )}
          </div>
        </div>
      </>
    );
  }

  if (screen.type === "cards" && Array.isArray(screen.items)) {
    return (
      <>
        {renderEditButton()}
        <div
          className="custom-container"
          style={{ padding: "24px 0", color: "#fff" }}
        >
          {screen.title && (
            <h2 style={{ marginBottom: 16, color: "#fff" }}>{screen.title}</h2>
          )}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: 16,
            }}
          >
            {screen.items.map((c, i) => (
              <div
                key={i}
                style={{
                  padding: 16,
                  border: "1px solid rgba(255,255,255,0.15)",
                  borderRadius: 10,
                  color: "#fff",
                }}
              >
                <h4 style={{ marginTop: 0, color: "#fff" }}>
                  {c.title || `Card ${i + 1}`}
                </h4>
                {c.text && (
                  <p style={{ opacity: 0.9, color: "#fff" }}>{c.text}</p>
                )}
                {c.link && c.link.href && (
                  <a
                    className="btn"
                    href={c.link.href}
                    target={c.link.target || "_self"}
                    rel="noreferrer"
                    style={{ color: "#fff" }}
                  >
                    {c.link.label || "Open"}
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      </>
    );
  }

  if (screen.type === "cta") {
    return (
      <>
        {renderEditButton()}
        <div
          className="custom-container"
          style={{ padding: "36px 0", textAlign: "center", color: "#fff" }}
        >
          <h2 style={{ marginBottom: 12, color: "#fff" }}>
            {screen.title || ""}
          </h2>
          <p style={{ opacity: 0.9, color: "#fff" }}>{screen.text || ""}</p>
          {screen.button && screen.button.href && (
            <a
              className="primary-gradient-btn"
              href={screen.button.href}
              target={screen.button.target || "_self"}
              rel="noreferrer"
            >
              {screen.button.label || "Continue"}
            </a>
          )}
        </div>
      </>
    );
  }

  // Fallback
  return (
    <>
      {renderEditButton()}
      <div
        className="custom-container"
        style={{ paddingTop: 24, color: "#fff" }}
      >
        <h2 style={{ color: "#fff" }}>{screen.title || routeKey}</h2>
        <pre style={{ whiteSpace: "pre-wrap", color: "#fff" }}>
          {JSON.stringify(screen, null, 2)}
        </pre>
      </div>
    </>
  );
}
